import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SectionRenderer from '../components/sections/SectionRenderer';
import Headers from '../components/Headers';
import Footer from '../components/Footer';

const StorePreview = () => {
    const [sections, setSections] = useState([]);
    const [seller, setSeller] = useState(null);
    const { userInfo } = useSelector(state => state.auth);

    useEffect(() => {
        const handleMessage = (event) => {
            // Verify origin if needed, but for local dev it's fine
            if (event.data.type === 'UPDATE_PREVIEW') {
                setSections(event.data.sections);
                if (event.data.seller) {
                    setSeller(event.data.seller);
                }
            }
        };

        window.addEventListener('message', handleMessage);

        // Signal that preview is ready
        window.parent.postMessage({ type: 'PREVIEW_READY' }, '*');

        return () => window.removeEventListener('message', handleMessage);
    }, []);

    // Mock seller object if not provided, to prevent crashes
    const effectiveSeller = seller || {
        _id: 'preview-id',
        shopInfo: {
            shopName: 'Preview Store',
            theme: 'light'
        }
    };

    // Apply theme
    useEffect(() => {
        const themeSettings = effectiveSeller?.themeSettings || effectiveSeller?.shopInfo?.themeSettings;
        const useCustomTheme = themeSettings?.useCustomTheme;

        // Apply light-theme class when using custom theme (defaults to light colors)
        // Or when shopInfo.theme is explicitly 'light' in default mode
        if (useCustomTheme || effectiveSeller?.shopInfo?.theme === 'light') {
            document.body.classList.add('light-theme');
        } else {
            document.body.classList.remove('light-theme');
        }

        // Only apply custom CSS variables if useCustomTheme is true
        if (useCustomTheme && themeSettings) {
            const tempRoot = document.body;
            const colors = themeSettings.colors || {};

            const setVar = (name, value) => {
                if (value) tempRoot.style.setProperty(name, value);
            };

            setVar('--store-bg', colors.background);
            // Text colors handled by CSS defaults based on light/dark theme
            setVar('--store-button-bg', colors.buttonBg);
            setVar('--store-button-text', colors.buttonText);
            setVar('--store-product-card-bg', colors.productCardBg);
            setVar('--store-header-bg', colors.headerBg);
            setVar('--store-footer-bg', colors.footerBg);
            setVar('--store-input-bg', themeSettings.inputs?.background || colors.inputBg);
            setVar('--store-input-text', themeSettings.inputs?.text || colors.inputText);
            setVar('--store-input-border', themeSettings.inputs?.border || colors.inputBorder);
            setVar('--store-input-border-width', `${themeSettings.inputs?.borderWidth || 1}px`);
            setVar('--store-input-focus', themeSettings.inputs?.focusRing || colors.accent || '#000000');
            setVar('--store-input-focus-width', `${themeSettings.inputs?.focusWidth || 1}px`);
            setVar('--store-input-radius', `${themeSettings.inputs?.radius || 20}px`);
            setVar('--store-select-bg', themeSettings.selects?.background || colors.inputBg);
            setVar('--store-select-text', themeSettings.selects?.text || colors.inputText);
            setVar('--store-select-border', themeSettings.selects?.border || colors.inputBorder);
            setVar('--store-select-border-width', `${themeSettings.selects?.borderWidth || 1}px`);
            setVar('--store-select-focus', themeSettings.selects?.focusRing || colors.accent || '#000000');
            setVar('--store-select-focus-width', `${themeSettings.selects?.focusWidth || 1}px`);
            setVar('--store-select-radius', `${themeSettings.selects?.radius || 20}px`);
            setVar('--store-border', colors.border);
            setVar('--store-muted', colors.muted);
            setVar('--store-glass', colors.glass);

            // Inject/Update dynamic CSS for focus states
            const styleId = 'store-dynamic-focus-styles';
            let styleEl = document.getElementById(styleId);
            if (!styleEl) {
                styleEl = document.createElement('style');
                styleEl.id = styleId;
                document.head.appendChild(styleEl);
            }
            styleEl.innerHTML = `
              .store-input-focus:focus, .store-input-focus:focus-visible {
                outline: none !important;
                box-shadow: 0 0 0 var(--store-input-focus-width, 1px) var(--store-input-focus, #000) !important;
              }
              .store-select-focus:focus, .store-select-focus:focus-visible,
              .store-select-focus[data-state="open"] {
                outline: none !important;
                box-shadow: 0 0 0 var(--store-select-focus-width, 1px) var(--store-select-focus, #000) !important;
              }
            `;

            // Load and apply Google Font
            if (themeSettings.typography?.fontFamily) {
                const fontFamily = themeSettings.typography.fontFamily;
                const fontId = `google-font-${fontFamily.replace(/\s+/g, '-')}`;

                // Check if font is already loaded
                if (!document.getElementById(fontId)) {
                    const link = document.createElement('link');
                    link.id = fontId;
                    link.rel = 'stylesheet';
                    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@300;400;500;600;700&display=swap`;
                    document.head.appendChild(link);
                }

                setVar('--store-font-family', `"${fontFamily}", sans-serif`);
                tempRoot.style.fontFamily = `"${fontFamily}", sans-serif`;
            }
        }
    }, [effectiveSeller]);

    // Intercept links and enable click-to-edit
    useEffect(() => {
        const handleClick = (e) => {
            // Check for editable inputs
            const input = e.target.closest('input, select, [data-editable]');
            if (input) {
                e.preventDefault();
                e.stopPropagation();
                // Determine what type of element was clicked
                let editTarget = 'inputs'; // default to inputs section
                const dataEditableId = input.getAttribute('data-editable');
                if (dataEditableId) {
                    editTarget = dataEditableId;
                }
                window.parent.postMessage({ type: 'EDIT_ELEMENT', target: editTarget }, '*');
                return;
            }

            const link = e.target.closest('a');
            if (link) {
                const href = link.getAttribute('href');
                // If it's a link to the store home, prevent default and scroll top
                if (href === '/' || href === `/${effectiveSeller.shopInfo.shopName}`) {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
            }
        };
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, [effectiveSeller]);

    return (
        <div className="min-h-screen bg-[var(--store-bg)]">
            {/* If header is in sections, SectionRenderer handles it. Otherwise show default */}
            {sections.some(s => s.type === 'header') ? (
                <SectionRenderer sections={sections} sellerId={effectiveSeller._id} seller={effectiveSeller} isPreview={true} />
            ) : (
                <>
                    <Headers storeName={effectiveSeller.shopInfo.shopName} isPreview={true} />
                    <SectionRenderer sections={sections} sellerId={effectiveSeller._id} seller={effectiveSeller} isPreview={true} />
                </>
            )}
            {!sections.some(s => s.type === 'footer') && <Footer />}
        </div>
    );
};

export default StorePreview;
